import {
  getBudgetItemEntryKindEnums,
  getLinkedPortfolioEnums,
  getOwner,
  getOwnerNewDynamicEnumLabel,
  getOwnerNewEnums,
} from 'src/pages/Budget/components/BudgetItemForm/BudgetItemForm.utils'
import { entryCategoriesEnumLabel } from 'src/services/mappings/budgetItem/budgetItems.utils'
import {
  enumLabelIsKey,
  maxHundredPercentRules,
} from 'src/services/mappings/mapping.utils'
import { getBudgetEntryKindLabel } from 'src/services/utils/budget.utils'
import type {
  DynamicEnumsRuleInterface,
  FieldInterface,
  RelationInterface,
} from 'src/tpbp-configuration/mapper/mappingInterfaces'
import { OBJECT_TYPES } from 'src/tpbp-configuration/models/models.const'
import {
  BUDGET_ITEMS_ENTRY_TYPES,
  BUDGET_UX_CONTEXTS,
  EXPENSE_CATEGORY,
} from 'src/tpbp-core/models/budget/budgetItem.const'
import {
  FIELD_SET_DISPLAY_TYPES,
  FIELD_TYPES,
} from 'src/tpbp-core/models/fields/field-set.const'
import { FIELD_RULES } from 'src/tpbp-core/models/fields/fieldRules.const'
import { TEXT_INPUT_FORMATS } from 'src/tpbp-core/models/fields/text-input.const'
import { budgetApiServiceStore } from 'src/tpbp-json-api-handler/budget/budgetApiServiceStore'
import type { BudgetItemModel } from 'src/tpbp-json-api-handler/models/models'
import { budgetItemFieldDynamicLabel } from './budget-items-dynamic-label'

const contextualDynamicReadyOnlyRule = {
  type: FIELD_RULES.isReadOnly,
  expression: (record: BudgetItemModel, _, { key }) =>
    key !== 'amount' &&
    record.entryKind === EXPENSE_CATEGORY &&
    record.$uxContext === BUDGET_UX_CONTEXTS.inTable,
}

const contextualDynamicHiddenRule = {
  type: FIELD_RULES.isHidden,
  expression: (record: BudgetItemModel) =>
    record.entryKind === EXPENSE_CATEGORY &&
    record.$uxContext !== BUDGET_UX_CONTEXTS.inTable,
}

const contextualDynamicRules = [
  {
    type: FIELD_RULES.isMandatory,
    expression: (record, { budgetFieldIsMandatory }, { originKey }) =>
      budgetFieldIsMandatory(record, originKey),
  },
  {
    type: FIELD_RULES.isOptional,
    expression: (record, { budgetFieldIsOptional }, { originKey }) =>
      budgetFieldIsOptional(record, originKey),
  },
  contextualDynamicReadyOnlyRule,
]

const contextualDynamicEnumRules: DynamicEnumsRuleInterface[] = [
  {
    expression: () => true,
    getEnumKeys: (record, { getBudgetItemEnums }, field) =>
      getBudgetItemEnums(record, field),
  },
]

export const contextualPortfolioRules = [
  {
    type: FIELD_RULES.isOptional,
    expression: ({ $uxContext, _linkablePortfolios }) =>
      $uxContext === BUDGET_UX_CONTEXTS.inTable &&
      _linkablePortfolios?.length > 0,
  },
  contextualDynamicReadyOnlyRule,
  contextualDynamicHiddenRule,
]

export const contextualOwnerRules = [
  {
    type: FIELD_RULES.isReadOnly,
    expression: ({ $isNew, $uxContext }) =>
      $uxContext === BUDGET_UX_CONTEXTS.inTable && !$isNew,
  },
]

export const contextualOwnerNewRules = [
  {
    type: FIELD_RULES.isMandatory,
    expression: ({ $isNew, $uxContext }) =>
      $uxContext === BUDGET_UX_CONTEXTS.inTable && $isNew,
  },
]

export const mandatoryInTableNewRecordRules = [
  {
    type: FIELD_RULES.isReadOnly,
    expression: (record: BudgetItemModel) =>
      record.$uxContext === BUDGET_UX_CONTEXTS.inTable && !record.$isNew,
  },
  {
    type: FIELD_RULES.isMandatory,
    expression: (record: BudgetItemModel) =>
      record.$uxContext === BUDGET_UX_CONTEXTS.inTable && record.$isNew,
  },
  {
    type: FIELD_RULES.isHidden,
    expression: (record: BudgetItemModel) =>
      record.$uxContext !== BUDGET_UX_CONTEXTS.inTable,
  },
]

const budgetItemFields: FieldInterface[] = [
  {
    key: 'entryKind',
    originKey: 'entry_kind',
    type: FIELD_TYPES.string,
    enumKeys: [],
    dynamicRules: [
      {
        type: FIELD_RULES.isReadOnly,
        expression: (record) => !record.$isNew,
      },
      {
        type: FIELD_RULES.isMandatory,
        expression: ({ $uxContext, $isNew, entryCategory }) => {
          const isInTable = $uxContext === BUDGET_UX_CONTEXTS.inTable
          return isInTable ? $isNew && Boolean(entryCategory) : true
        },
      },
      contextualDynamicHiddenRule,
    ],
    getEnums: getBudgetItemEntryKindEnums,
    getEnumLabel: getBudgetEntryKindLabel,
  },
  {
    key: 'entryCategory',
    originKey: 'entry_category',
    type: FIELD_TYPES.string,
    enumKeys: [],
    enumRules: contextualDynamicEnumRules,
    dynamicEnumLabel: (budgetItem) => {
      const categories = budgetApiServiceStore.getCategoriesByEntryCategory()
      return entryCategoriesEnumLabel(budgetItem, categories)
    },
    dynamicRules: mandatoryInTableNewRecordRules,
  },
  {
    key: 'displayedCategory',
    originKey: 'entry_category_front_end',
    type: FIELD_TYPES.string,
    enumKeys: [],
    enumRules: contextualDynamicEnumRules,
    dynamicEnumLabel: enumLabelIsKey,
    dynamicRules: [
      {
        type: FIELD_RULES.isReadOnly,
        expression: (record: BudgetItemModel) =>
          record.$uxContext === BUDGET_UX_CONTEXTS.inTable && !record.$isNew,
      },
      {
        type: FIELD_RULES.isHidden,
        expression: (record: BudgetItemModel) =>
          record.$uxContext === BUDGET_UX_CONTEXTS.inTable && record.$isNew,
      },
      {
        type: FIELD_RULES.isHidden,
        expression: (record: BudgetItemModel) =>
          record.$uxContext !== BUDGET_UX_CONTEXTS.inTable,
      },
    ],
  },
  {
    key: 'entryType',
    originKey: 'entry_type',
    type: FIELD_TYPES.string,
    enumKeys: Object.values(BUDGET_ITEMS_ENTRY_TYPES),
    rule: FIELD_RULES.isHidden,
  },
  {
    key: 'label',
    originKey: 'label',
    default: '',
    type: FIELD_TYPES.string,
    dynamicRules: [...contextualDynamicRules, contextualDynamicHiddenRule],
  },
  {
    key: 'amount',
    originKey: 'amount',
    type: FIELD_TYPES.number,
    default: 0,
    getHelpMessageKey: (record) =>
      record?.entryKind === EXPENSE_CATEGORY
        ? 'models.budget_items.helpMessage.amount'
        : '',
    format: TEXT_INPUT_FORMATS.currency,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'estimate',
    originKey: 'estimate',
    isPseudoAttribute: true,
    type: FIELD_TYPES.button,
    dynamicRules: [
      {
        type: FIELD_RULES.isMandatory,
        expression: (record: BudgetItemModel) =>
          record.entryKind === EXPENSE_CATEGORY &&
          record.$uxContext !== BUDGET_UX_CONTEXTS.inTable,
      },
    ],
    event: 'estimate',
  },
  {
    key: 'frequency',
    originKey: 'frequency',
    type: FIELD_TYPES.string,
    default: 'monthly',
    enumKeys: ['single', 'monthly', 'quarterly', 'biannually', 'yearly'],
    dynamicRules: [...contextualDynamicRules, contextualDynamicHiddenRule],
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'indexedPct',
    originKey: 'indexed_pct',
    type: FIELD_TYPES.number,
    dynamicRules: [...contextualDynamicRules, contextualDynamicHiddenRule],
    getRules: maxHundredPercentRules,
  },
  {
    key: 'fromType',
    originKey: 'from_type',
    type: FIELD_TYPES.string,
    enumKeys: ['at_date', 'start_date', 'acquisition_date'],
    dynamicRules: contextualDynamicRules,
    displayBreak: true,
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'fromDate',
    originKey: 'from_date',
    type: FIELD_TYPES.date,
    dynamicRules: [
      {
        type: FIELD_RULES.isMandatory,
        expression: (
          record: BudgetItemModel,
          { BUDGET_ITEM_FROM_TYPE_ASSOCIATION },
        ) => BUDGET_ITEM_FROM_TYPE_ASSOCIATION[record.fromType] === 'fromDate',
      },
      {
        type: FIELD_RULES.isOptional,
        expression: (
          record: BudgetItemModel,
          { BUDGET_ITEM_FROM_TYPE_ASSOCIATION },
        ) => BUDGET_ITEM_FROM_TYPE_ASSOCIATION[record.fromType] !== 'fromDate',
      },
    ],
  },
  {
    key: 'toType',
    originKey: 'to_type',
    type: FIELD_TYPES.string,
    default: 'undetermined',
    dynamicRules: contextualDynamicRules,
    displayBreak: true,
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'toValue',
    originKey: 'to_value',
    type: FIELD_TYPES.integer,
    dynamicRules: [
      {
        type: FIELD_RULES.isMandatory,
        expression: (
          record: BudgetItemModel,
          { BUDGET_ITEM_TO_TYPE_ASSOCIATION },
          { key },
        ) => BUDGET_ITEM_TO_TYPE_ASSOCIATION[record.toType] === key,
      },
    ],
  },
  {
    key: 'toDate',
    originKey: 'to_date',
    type: FIELD_TYPES.date,
    dynamicRules: [
      {
        type: FIELD_RULES.isMandatory,
        expression: (
          record: BudgetItemModel,
          { BUDGET_ITEM_TO_TYPE_ASSOCIATION },
          { key },
        ) => BUDGET_ITEM_TO_TYPE_ASSOCIATION[record.toType] === key,
      },
    ],
  },
  {
    key: 'yearlyRealFees',
    originKey: 'yearly_real_fees',
    dynamicRules: contextualDynamicRules,
    type: FIELD_TYPES.number,
  },
  {
    key: 'hasPlanningImpact',
    originKey: 'has_planning_impact',
    dynamicRules: contextualDynamicRules,
    type: FIELD_TYPES.boolean,
  },
  {
    key: 'yearlyTaxableIncome',
    originKey: 'yearly_taxable_income',
    dynamicRules: contextualDynamicRules,
    type: FIELD_TYPES.number,
    dynamicLabel: budgetItemFieldDynamicLabel,
  },
  {
    key: 'reversionRate',
    originKey: 'reversion_rate',
    dynamicRules: contextualDynamicRules,
    type: FIELD_TYPES.number,
    getRules: maxHundredPercentRules,
  },
  {
    key: 'lumpSumDeduction',
    originKey: 'lump_sum_deduction',
    type: FIELD_TYPES.string,
    enumKeys: [
      'deducted_at_source',
      'csg_deducted_at_source',
      'charges_deducted_at_source',
    ],
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'plan',
    originKey: 'plan',
    type: FIELD_TYPES.string,
    enumKeys: [
      'ordinary',
      'micro_land',
      'old_borloo_intermediate',
      'old_borloo_social',
      'besson_old_housing',
    ],
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'buildingPermitFiledDate',
    originKey: 'building_permit_filed_date',
    type: FIELD_TYPES.date,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'ownershipCode',
    originKey: 'ownership_code',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isHidden,
    dependentFieldKeys: ['owner'],
  },
  {
    key: 'person1OwnershipPct',
    originKey: 'person_1_ownership_pct',
    type: FIELD_TYPES.number,
    displayType: FIELD_SET_DISPLAY_TYPES.sliderRepartition,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'isExpired',
    originKey: 'isExpired',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isHidden,
    isPseudoAttribute: true,
  },
  {
    key: 'taxSystem',
    originKey: 'tax_system',
    type: FIELD_TYPES.string,
    enumKeys: [
      'ba_avec_cga',
      'ba_sans_cga',
      'ba_forfait',
      'ba_sans_cga_real',
      'ba_exonere_avec_cga',
      'ba_exonere_sans_cga',
      'bic_avec_cga',
      'bic_sans_cga',
      'bic_micro_reventes',
      'bic_micro',
      'bic_np_avec_cga',
      'bic_np_sans_cga',
      'bic_lmnp_micro',
      'bic_np_micro_services',
      'bic_exonere_avec_cga',
      'bic_exonere_sans_cga',
      'bic_micro_services',
      'bic_lmp_sans_cga',
      'bic_lmnp_micro_vente',
      'bic_np',
      'bic_lmnp_micro_tourisme',
      'bnc_avec_cga',
      'bnc_sans_cga',
      'bnc_sans_cga_special',
      'bnc_np_avec_cga',
      'bnc_np_sans_cga',
      'bnc_np_sans_cga_sepcial',
      'bnc_exonere_avec_cga',
      'bnc_exonere_sans_cga',
      'bnc_micro',
      'bnc_sans_cga_real',
      'bnc_np_micro',
      'bnc_np_sans_cga_real',
    ],
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'linkedPortfolio',
    originKey: 'linkedPortfolio',
    targetKey: 'id',
    originRelationship: '_linkedPortfolios',
    type: FIELD_TYPES.string,
    dynamicRules: contextualPortfolioRules,
    getEnums: getLinkedPortfolioEnums,
    isPseudoAttribute: true,
    isClearable: true,
  },
  {
    key: 'owner',
    originKey: 'owner',
    targetKey: 'id',
    type: FIELD_TYPES.string,
    dynamicRules: contextualOwnerRules,
    expression: getOwner,
    isPseudoAttribute: true,
  },
  {
    key: 'ownerNew',
    originKey: 'ownerNew',
    targetKey: 'id',
    type: FIELD_TYPES.string,
    dynamicRules: contextualOwnerNewRules,
    getEnums: getOwnerNewEnums,
    dynamicEnumLabel: getOwnerNewDynamicEnumLabel,
    isPseudoAttribute: true,
  },
]

const budgetItemRelations: RelationInterface[] = [
  {
    key: '_titulars',
    originKey: 'titulars',
    objectType: OBJECT_TYPES.person,
    isMultiple: true,
  },
  {
    key: '_titularsById',
    originKey: 'titulars',
    objectType: OBJECT_TYPES.person,
    isMultiple: true,
    byId: true,
  },
  {
    key: '_portfolios',
    originKey: 'portfolios',
    objectType: OBJECT_TYPES.portfolio,
    isMultiple: true,
  },
  {
    key: '_linkablePortfolios',
    originKey: 'linkable_portfolios',
    objectType: OBJECT_TYPES.portfolio,
    isMultiple: true,
  },
  {
    key: '_linkedPortfolios',
    originKey: 'linked_portfolios',
    objectType: OBJECT_TYPES.portfolio,
    isMultiple: true,
  },
  {
    key: '_profession',
    originKey: 'profession',
    objectType: OBJECT_TYPES.profession,
    isMultiple: false,
  },
]

export { budgetItemFields, budgetItemRelations }
